import type { ProjectFilterActions } from "@/features/projects/filter/projectFilterReducer";
import styled from "@emotion/styled";
import {
  type OpportunitiesSearchFilters,
  type OpportunitiesSearchOptions,
  generateCoworkerPortfolioBrief,
  useCoworkerPortfolioBrief,
} from "@repo/api";
import {
  Button,
  LightMode,
  Text,
  Variables,
  chevronDownIcon,
  chevronUpIcon,
} from "@repo/skapa";
import {
  DEFAULT_LOCALE,
  getLanguageAndLocaleFromUrl,
  t,
} from "@repo/translation";
import {
  STORAGE_KEY,
  getStorageItem,
  setStorageItem,
} from "@repo/ui/src/storage/storage";
import { useState } from "react";

import { Categories } from "./Categories";
import { NextSteps } from "./NextSteps";
import { Summary } from "./Summary";

const ButtonRow = styled.div`
  display: flex;
  gap: ${Variables.space50};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  background-color: ${LightMode.colourNeutral1};
  border: 1px solid ${LightMode.colourNeutral3};
  border-radius: ${Variables.radiusM};
  padding: ${Variables.space100};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Content = styled.div<{
  isOpen: boolean;
}>`
  padding-top: ${(props) => (props.isOpen ? Variables.space100 : 0)};
  display: grid;
  grid-template-rows: ${(props) => (props.isOpen ? "1fr" : "0fr")};
  overflow: hidden;
  transition: all ${Variables.durationStandardWeb} ${Variables.easeEasy};
`;

const InnerContent = styled.div`
  min-height: 0;

  & > :not(:last-child) {
    &:after {
      content: "";
      display: block;
      width: 100%;
      margin-top: ${Variables.space100};
      margin-bottom: ${Variables.space100};
      border-bottom: 1px solid ${LightMode.colourNeutral3};
    }
  }
`;

const getCollapsibleStateFromStorage = () => {
  const res = getStorageItem(STORAGE_KEY.PORTFOLIO_AI_SUMMARY);

  if (!res) {
    return true;
  }
  return JSON.parse(res) as boolean;
};

interface ProjectsSummaryProps {
  totalSize?: number;
  filters: OpportunitiesSearchFilters;
  options: OpportunitiesSearchOptions;
  actions: ProjectFilterActions;
}

export const PortfolioSummary = ({
  totalSize,
  filters,
  options,
  actions,
}: ProjectsSummaryProps) => {
  const [isCollapsibleOpen, setIsCollapsibleOpen] = useState<boolean>(
    getCollapsibleStateFromStorage
  );

  const { data, isValidating, isLoading, mutate } = useCoworkerPortfolioBrief(
    filters,
    options
  );

  const locale = getLanguageAndLocaleFromUrl();
  const { language } = locale ?? DEFAULT_LOCALE;

  const isProcessing = data?.status === "PROCESSING";
  const hasSummary = Boolean(data?.response?.brief?.[language]);
  const isBusy = isProcessing || isValidating || isLoading;

  const handleCollapsibleClick = () => {
    const newValue = !isCollapsibleOpen;

    setIsCollapsibleOpen(newValue);
    setStorageItem(STORAGE_KEY.PORTFOLIO_AI_SUMMARY, JSON.stringify(newValue));
  };

  const handleGenerate = async () => {
    try {
      await generateCoworkerPortfolioBrief(filters, options);
      await mutate();

      setIsCollapsibleOpen(true);
      setStorageItem(STORAGE_KEY.PORTFOLIO_AI_SUMMARY, JSON.stringify(true));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to generate portfolio summary", error);
    }
  };

  if (!totalSize) {
    return null;
  }

  return (
    <Container>
      <Header>
        <Text textStyle="Heading.XS">{t("label.ai_portfolio_title")}</Text>
        <ButtonRow>
          <Button
            onClick={handleGenerate}
            disabled={isBusy}
            type="primary"
            size="small"
          >
            {isProcessing
              ? t("prompt.generating_summary")
              : hasSummary
                ? t("action.regenerate_summary")
                : t("action.generate_summary")}
          </Button>
          {hasSummary && (
            <Button
              ssrIcon={isCollapsibleOpen ? chevronUpIcon : chevronDownIcon}
              iconOnly
              size="small"
              onClick={handleCollapsibleClick}
              text={
                isCollapsibleOpen
                  ? t("action.collapse_section")
                  : t("action.expand_section")
              }
            />
          )}
        </ButtonRow>
      </Header>

      <Content isOpen={isCollapsibleOpen}>
        <InnerContent>
          {data ? (
            isProcessing ? (
              <Text>{t("prompt.summary_generating")}</Text>
            ) : (
              <>
                <Summary
                  summary={
                    data.response?.brief?.[language] ??
                    t("prompt.no_summary_available")
                  }
                />

                <NextSteps steps={data.response?.actions ?? []} />

                <Categories
                  categories={data.response?.projectCategorisation ?? []}
                  actions={actions}
                  filters={filters}
                />
              </>
            )
          ) : (
            <Text>{t("prompt.no_summary_cta")}</Text>
          )}
        </InnerContent>
      </Content>
    </Container>
  );
};
